import React, { useState, useEffect, useRef, useCallback } from "react";
import { Combine, LineChart as LineChartIcon, Users, Car, Activity, Map, KeyRound, Bell, PlusCircle } from "lucide-react"; 
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom"; 

// ✅ FIX: Correct Path to SendCommandModal (It is likely in src/components/)
import SendCommandModal from '../SendCommandModal'; 

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// ✅ FIX: Imports are in the SAME folder, so use "./"
import useGroupAdminData from "./useGroupAdminData";
import useGroupAdminHandlers from "./useGroupAdminHandlers";

import GroupAdminSidebar from "./GroupAdminSidebar";
import GroupAdminOverview from "./GroupAdminOverview";
import ManagedUsersTab from "./ManagedUsersTab";
import ManagedDevicesTab from "./ManagedDevicesTab";
import GroupMapTracker from "./GroupMapTracker";
import GroupEventLogs from "./GroupEventLogs";

// ✅ FIX: Admin components are one level up in "admin" folder ("../admin/")
import AssetManagement from "../admin/AssetManagement"; 
import AddAssetModal from "../admin/AddAssetModal";     
import EditAssetModal from "../admin/EditAssetModal";   
// import StaticsDashboard from "../admin/StaticsDashboard"; // Uncomment if needed

// ✅ FIX: Modals are likely in "src/components/admin/" based on your previous code
// If CreateManagedUserModal is specific to group admin, check if it's in ./ or ../admin
import CreateManagedUserModal from "../groupadmin/CreateManagedUserModal";
import EditManagedUserModal from "../groupadmin/EditManagedUserModal";
import CreateDeviceModal from "../groupadmin/CreateDeviceModal";
import EditManagedDeviceModal from "../groupadmin/EditManagedDeviceModal"; // Fixed typo "ed"
import AssignDeviceModal from "../groupadmin/AssignDeviceModal"; // Fixed empty import
import GeofenceModal from "../groupadmin/GeofenceModal";
import { verifyRelayAccess } from "../../utils/relayAccess";

import './groupadmindashboard.css'; // Assuming css is in same folder

// ✅ FIX: Store is likely two levels up ("../../store/authSlice")
import { logout } from "../../store/authSlice"; 

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function GroupAdminDashboard() {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const handleLogout = () => dispatch(logout());
  const audioRef = useRef(null);
  const prevEventsRef = useRef();
  const didFetchRef = useRef(false);

  // Global State
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  
  const setMessageWithTimeout = useCallback((setter, message) => {
    setter(message);
    setTimeout(() => setter(""), 3000);
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem("groupAdminDashboardActiveTab");
    return savedTab || "statics"; 
  });

  useEffect(() => {
    localStorage.setItem("groupAdminDashboardActiveTab", activeTab);
  }, [activeTab]);

  // Map State
  const [deviceAddresses, setDeviceAddresses] = useState({});
  const [selectedDevices, setSelectedDevices] = useState(() => {
    const saved = localStorage.getItem("groupAdminSelectedDevices");
    return saved ? JSON.parse(saved) : [];
  });
  const [routeStart, setRouteStart] = useState(null);
  const [routeEnd, setRouteEnd] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [showFilter, setShowFilter] = useState(true);

  // --- Modal States ---
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [showAssignDeviceModal, setShowAssignDeviceModal] = useState(false);
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [showCommandModal, setShowCommandModal] = useState(false);
  
  // Asset Modals
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showEditAssetModal, setShowEditAssetModal] = useState(false);

  // --- Selected Items ---
  const [editingUser, setEditingUser] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedDeviceForAssignment, setSelectedDeviceForAssignment] = useState(null);
  const [selectedDeviceForGeofence, setSelectedDeviceForGeofence] = useState(null);
  const [deviceForCommand, setDeviceForCommand] = useState(null);
  const [unsavedGeofence, setUnsavedGeofence] = useState(null);
  const [geofenceName, setGeofenceName] = useState("");

  // --- Form States ---
  const [newUserData, setNewUserData] = useState({
    username: "", email: "", mobileNo: "", password: "", role: "user",
    isActive: true
  });

  const [newDeviceData, setNewDeviceData] = useState({
    deviceId: "", name: "", model: "", vehicleType: "", imei: "",
    msdn: "", imsi: "", deviceType: "GT06", assignedToUser: null,
    assignedToViewers: []
  });

  const [newAssetData, setNewAssetData] = useState({
    IMEI: "", serialNumber: "", model: "", type: "hardwire", status: "unassigned",
    simCard: { IMSI: "", MSDN: "", provider: "", expiryDate: "" },
    purchaseDate: "", notes: "",
  });

  // Assignment Tab Local State
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [assignToUserId, setAssignToUserId] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Command Modal Handlers
  const handleOpenCommandModal = useCallback(async (device, options = {}) => {
    try {
      const access = await verifyRelayAccess(options);
      if (!access?.ok) {
        setMessageWithTimeout(setErrorMessage, access?.message || "Relay access denied.");
        return;
      }
    } catch (error) {
      setMessageWithTimeout(
        setErrorMessage,
        error?.response?.data?.msg || error?.message || "Failed to verify relay access."
      );
      return;
    }

    setDeviceForCommand(device);
    setShowCommandModal(true);
  }, [setMessageWithTimeout]);

  const handleCloseCommandModal = useCallback(() => {
    setDeviceForCommand(null);
    setShowCommandModal(false);
  }, []);

  // Role Check & Redirect
  useEffect(() => {
    if (currentUser && !["group_admin", "manager", "hr"].includes(currentUser.role)) {
      const role = currentUser.role;
      if (role === 'admin') {
        navigate("/dashboard", { replace: true });
      } else if (["user", "client"].includes(role)) {
        navigate("/individual-dashboard", { replace: true });
      } else if (role === "viewer") {
        navigate("/viewer-dashboard", { replace: true });
      } else {
        handleLogout();
      }
    }
  }, [currentUser, navigate, handleLogout]);

  const skipDataFetch = !currentUser || !["group_admin", "manager", "hr"].includes(currentUser.role);

  // Data Hook
  const {
    loading, error, managedUsers, managedDevices, devicesOnMap,
    deviceCounts, deviceEvents, eventLoading, eventError,
    // Assets
    assetsData, isAssetsLoading, assetError, fetchAllAssets,
    fetchGroupAdminData, fetchLiveData, fetchDeviceEvents
  } = useGroupAdminData({
    currentUser, handleLogout, setMessageWithTimeout,
    setErrorMessage, prevEventsRef, didFetchRef,
    setDeviceAddresses, setSelectedDevices, activeTab,
    selectedDevices, audioRef, skipFetch: skipDataFetch
  });

  // Handlers Hook
  const handlers = useGroupAdminHandlers({
    currentUser, backendUrl, handleLogout,
    setMessageWithTimeout, setSuccessMessage, setErrorMessage,
    fetchGroupAdminData, editingUser, setEditingUser,
    editingDevice, setEditingDevice, newUserData, setNewUserData,
    newDeviceData, setNewDeviceData, setShowCreateUserModal,
    setShowEditUserModal, setShowCreateDeviceModal,
    setShowEditDeviceModal, setShowAssignDeviceModal,
    setShowGeofenceModal, selectedDeviceForGeofence,
    setSelectedDeviceForGeofence, unsavedGeofence,
    geofenceName, setUnsavedGeofence, setGeofenceName,
    setRouteStart, setRouteEnd, setRouteDetails,
    handleOpenCommandModal, handleCloseCommandModal,
    
    // Asset & Assignment Props
    fetchAllAssets, editingAsset, setEditingAsset, setShowEditAssetModal,
    newAssetData, setNewAssetData, setShowAddAssetModal,
    assignDeviceId, setAssignDeviceId, assignToUserId, setAssignToUserId,
    bulkFile, setBulkFile, uploading, setUploading
  });

  const {
    handleCreateUser, handleEditUser, handleDeleteUser,
    handleCreateDevice, handleEditDevice, handleDeleteDevice,
    handleAssignDevice, handleGeofenceManagement,
    handleSaveGeofence, onGeofenceCreated, onGeofenceEdited,
    onGeofenceDeleted, getDirections, formatTimestamp12h, 
    getVehicleIcon, vehicleTypeIcons,
    // Asset Handlers
    handleAddAsset, handleEditAsset, handleDeleteAsset, 
    handlePermanentDeleteAsset, handleRestoreAsset,
    handleDownloadTemplate, handleBulkFileChange, handleBulkUpload, handleExportToExcel
  } = handlers;

  // Cleanup
  useEffect(() => {
    if (activeTab !== "map") {
      setRouteStart(null);
      setRouteEnd(null);
      setRouteDetails(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const isManager = currentUser?.role === 'Manager';
  const canShowInventory = currentUser?.role !== "group_admin" && currentUser?.role !== "dealer";

  useEffect(() => {
    if (!canShowInventory && activeTab === "assignments") {
      setActiveTab("statics");
    }
  }, [activeTab, canShowInventory]);

  // Conditional Renders
  if (!currentUser || !["group_admin", "manager", "hr"].includes(currentUser.role)) {
    return <p className="loading-spinner">Redirecting to your dashboard...</p>;
  }

  if (loading) return <p className="loading-spinner">Loading Dashboard...</p>;
  if (error) return <p className="error-message">Error: {error}</p>;

  const getTabTitle = (tab) => {
    switch (tab) {
      case "statics": return { title: "Dashboard Overview", icon: <LineChartIcon size={20} /> }; 
      case "map": return { title: "Live Map Tracking", icon: <Map size={20} /> };
      case "users": return { title: "Managed Users", icon: <Users size={20} /> };
      case "devices": return { title: "Managed Devices", icon: <Car size={20} /> };
      case "assignments": return { title: "Assets & Assignments", icon: <Combine size={20} /> }; 
      case "events": return { title: "Event Logs", icon: <Activity size={20} /> };
      default: return { title: "Dashboard", icon: <Activity size={20} /> };
    }
  };

  const { title: tabTitle, icon: tabIcon } = getTabTitle(activeTab);

  return (
    <div className="groupadmin-dashboard-container">
      <audio ref={audioRef} src="/assets/event.wav" muted autoPlay preload="auto"></audio>
      
      {/* Sidebar */}
      <GroupAdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      {/* Main Content */}
      <div className="main-content-wrapper">
        
        {/* Global Messages */}
        {successMessage && (<div className="global-alert success show">{successMessage}</div>)}
        {errorMessage && (<div className="global-alert error show">{errorMessage}</div>)}

        {/* Tab Content */}
        <main className="tab-content-area">
          
          {/* Overview Tab */}
          {(activeTab === "statics" || activeTab === "overview") && (
             <GroupAdminOverview
               deviceCounts={deviceCounts}
               managedUsers={managedUsers}
               managedDevices={managedDevices}
               setShowCreateUserModal={setShowCreateUserModal}
               setShowCreateDeviceModal={setShowCreateDeviceModal}
             />
          )}

          {/* Map Tracker Tab */}
          {activeTab === "map" && (
            <GroupMapTracker
              devicesOnMap={devicesOnMap}
              selectedDevices={selectedDevices}
              setSelectedDevices={setSelectedDevices}
              deviceAddresses={deviceAddresses}
              getDirections={getDirections}
              formatTimestamp12h={formatTimestamp12h}
              handleOpenCommandModal={handleOpenCommandModal}
              routeStart={routeStart}
              routeEnd={routeEnd}
              routeDetails={routeDetails}
              setRouteDetails={setRouteDetails}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              deviceCounts={deviceCounts}
              vehicleTypeIcons={vehicleTypeIcons}
              getVehicleIcon={getVehicleIcon}
            />
          )}

          {/* Managed Users Tab */}
          {activeTab === "users" && (
            <ManagedUsersTab
              managedUsers={managedUsers}
              setEditingUser={setEditingUser}
              setShowEditUserModal={setShowEditUserModal}
              handleDeleteUser={handleDeleteUser}
              setShowCreateUserModal={setShowCreateUserModal}
            />
          )}

          {/* Managed Devices Tab */}
          {activeTab === "devices" && (
            <ManagedDevicesTab
              managedDevices={managedDevices}
              managedUsers={managedUsers}
              formatTimestamp12h={formatTimestamp12h}
              setEditingDevice={setEditingDevice}
              setShowEditDeviceModal={setShowEditDeviceModal}
              handleGeofenceManagement={handleGeofenceManagement}
              handleDeleteDevice={handleDeleteDevice}
              setShowCreateDeviceModal={setShowCreateDeviceModal}
              setSelectedDeviceForAssignment={setSelectedDeviceForAssignment}
              setShowAssignDeviceModal={setShowAssignDeviceModal}
              handleOpenCommandModal={handleOpenCommandModal}
            />
          )}

          {/* Assets & Assignments Tab */}
          {canShowInventory && activeTab === "assignments" && (
            <div className="content-grid-2-1"> 
                <section className="card assignment-form-section">
                    <h2 className="card-title">Device Assignment</h2>
                    
                    <div className="assignment-forms-grid"> 
                        {/* Device Assignment Form */}
                        <div className="assignment-form-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            <h3 className="form-sub-title">Assign Device to Managed User</h3>
                            <form onSubmit={(e) => { e.preventDefault(); handleAssignDevice(assignDeviceId, assignToUserId); }}>
                                {/* Device Input */}
                                <div className="form-group-custom">
                                    <label htmlFor="assignDeviceInput">Select Device</label>
                                    <input type="text" id="assignDeviceInput" value={deviceSearchQuery} onChange={(e) => { setDeviceSearchQuery(e.target.value); if (e.target.value === "") setAssignDeviceId(""); }} placeholder="Type device name or ID" autoComplete="off" required/>
                                    {deviceSearchQuery && (
                                        <ul className="search-dropdown">
                                            {managedDevices.filter(device => `${device.name} ${device.deviceId}`.toLowerCase().includes(deviceSearchQuery.toLowerCase()))
                                                .slice(0, 5).map((device) => (
                                                    <li key={device._id} onClick={() => { setAssignDeviceId(device._id); setDeviceSearchQuery(`${device.name} (ID: ${device.deviceId})`);}}>
                                                        {device.name} (ID: {device.deviceId}) - {device.assignedToUser ? `User: ${device.assignedToUser.username}` : "Unassigned"}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                                {/* User Input */}
                                <div className="form-group-custom">
                                    <label htmlFor="assignToUserInput">Assign to User</label>
                                    <input type="text" id="assignToUserInput" value={userSearchQuery} onChange={(e) => { setUserSearchQuery(e.target.value); if (e.target.value === "") setAssignToUserId(""); }} placeholder="Type username, email, or mobile" autoComplete="off" required/>
                                    {userSearchQuery && (
                                        <ul className="search-dropdown">
                                            {managedUsers.filter(user => `${user.username} ${user.email} ${user.mobileNo}`.toLowerCase().includes(userSearchQuery.toLowerCase()))
                                                .slice(0, 5).map((user) => (
                                                    <li key={user._id} onClick={() => { setAssignToUserId(user._id); setUserSearchQuery(`${user.username} (${user.mobileNo || user.email})`);}}>
                                                        {user.username} ({user.mobileNo || user.email}) - {user.role}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                                <button type="submit" className="btn-primary full-width">Assign Device</button>
                            </form>
                        </div>
                    </div>
                </section>

                <section className="card quick-actions-card">
                    <h2 className="card-title">Quick Actions</h2>
                    <div className="quick-action-button-grid flex-wrap-2-col"> 
                        {/* CRUD for Devices, Users, Assets - Available to Manager & Group Admin */}
                        {currentUser.devicePermissions?.create && (<button className="btn-secondary-action" onClick={() => setShowCreateDeviceModal(true)}><PlusCircle size={18} /> Add Device</button>)}
                        {currentUser.userPermissions?.create && (<button className="btn-secondary-action" onClick={() => setShowCreateUserModal(true)}><Users size={18} /> Create User</button>)}
                        {currentUser.devicePermissions?.create && (<button className="btn-secondary-action" onClick={() => setShowAddAssetModal(true)}><Combine size={18} /> Add Assets</button>)}
                        
                        {/* Protocols - Available to Everyone */}
                        <Link to="/protocols" className="btn-secondary-action"><KeyRound size={18} /> Protocols</Link>
                        
                        <button className="btn-secondary-action" onClick={() => console.log('View Alerts')}><Bell size={18} /> View Alerts</button>

                        {/* RESTRICTED: Hide if Manager */}
                        {!isManager && (
                          <>
                            <Link to="/credits" className="btn-secondary-action"><KeyRound size={18} /> Transfer Credits</Link>
                            <Link to="/message" className="btn-secondary-action"><KeyRound size={18} /> Message Management</Link>
                          </>
                        )}
                    </div>
                </section>

                {/* Asset Management Table */}
                <div className="full-width-section" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                  <AssetManagement 
                      assetsData={assetsData} 
                      isAssetsLoading={isAssetsLoading} 
                      assetError={assetError}
                      handleDownloadTemplate={handleDownloadTemplate} 
                      handleBulkFileChange={handleBulkFileChange}
                      handleBulkUpload={handleBulkUpload} 
                      handleExportToExcel={handleExportToExcel} 
                      setEditingAsset={setEditingAsset}
                      setShowEditAssetModal={setShowEditAssetModal} 
                      handleRestoreAsset={handleRestoreAsset}
                      handlePermanentDeleteAsset={handlePermanentDeleteAsset} 
                      handleDeleteAsset={handleDeleteAsset}
                      bulkFile={bulkFile} 
                      uploading={uploading} 
                      setMessageWithTimeout={setMessageWithTimeout}
                      setSuccessMessage={setSuccessMessage} 
                      setErrorMessage={setErrorMessage}
                  />
                </div>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === "events" && (
            <GroupEventLogs
              deviceEvents={deviceEvents}
              eventLoading={eventLoading}
              eventError={eventError}
              formatTimestamp12h={formatTimestamp12h}
            />
          )}

        </main>
      </div>

      {/* --- Modals --- */}
      
      {/* User & Device Modals */}
      {showCreateUserModal && (
        <CreateManagedUserModal
          newUserData={newUserData}
          setNewUserData={setNewUserData}
          handleCreateUser={handleCreateUser}
          setShowCreateUserModal={setShowCreateUserModal}
        />
      )}

      {showEditUserModal && editingUser && (
        <EditManagedUserModal
          editingUser={editingUser}
          setEditingUser={setEditingUser}
          handleEditUser={handleEditUser}
          setShowEditUserModal={setShowEditUserModal}
        />
      )}

      {showCreateDeviceModal && (
        <CreateDeviceModal
          newDeviceData={newDeviceData}
          setNewDeviceData={setNewDeviceData}
          managedUsers={managedUsers}
          handleCreateDevice={handleCreateDevice}
          setShowCreateDeviceModal={setShowCreateDeviceModal}
        />
      )}

      {showEditDeviceModal && editingDevice && (
        <EditManagedDeviceModal
          editingDevice={editingDevice}
          setEditingDevice={setEditingDevice}
          managedUsers={managedUsers}
          handleEditDevice={handleEditDevice}
          setShowEditDeviceModal={setShowEditDeviceModal}
        />
      )}

      {/* Assignment Modal */}
      {showAssignDeviceModal && selectedDeviceForAssignment && (
        <AssignDeviceModal
          device={selectedDeviceForAssignment}
          managedUsers={managedUsers}
          handleAssignDevice={(deviceId, userId) => handleAssignDevice(deviceId, userId)}
          setShowAssignDeviceModal={setShowAssignDeviceModal}
        />
      )}

      {/* Geofence & Command */}
      {showGeofenceModal && selectedDeviceForGeofence && (
        <GeofenceModal
          selectedDeviceForGeofence={selectedDeviceForGeofence}
          geofenceName={geofenceName}
          setGeofenceName={setGeofenceName}
          onGeofenceCreated={onGeofenceCreated}
          onGeofenceEdited={onGeofenceEdited}
          onGeofenceDeleted={onGeofenceDeleted}
          unsavedGeofence={unsavedGeofence}
          handleSaveGeofence={handleSaveGeofence}
          setShowGeofenceModal={setShowGeofenceModal}
        />
      )}

      {showCommandModal && deviceForCommand && (
        <SendCommandModal
          device={deviceForCommand}
          onClose={handleCloseCommandModal}
        />
      )}

      {/* --- ASSET MODALS (New) --- */}
      {showAddAssetModal && (
        <AddAssetModal 
          newAssetData={newAssetData} 
          setNewAssetData={setNewAssetData} 
          handleAddAsset={handleAddAsset} 
          setShowAddAssetModal={setShowAddAssetModal}
        />
      )}
      
      {showEditAssetModal && editingAsset && (
        <EditAssetModal 
          editingAsset={editingAsset} 
          setEditingAsset={setEditingAsset} 
          handleEditAsset={handleEditAsset} 
          setShowEditAssetModal={setShowEditAssetModal}
        />
      )}

    </div>
  );
}

export default GroupAdminDashboard;
